import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api, type OrderRowDTO } from '../lib/api'
import { CustomerAvatar } from '../components/CustomerAvatar'
import { ChannelMark, Icon } from '../components/icons'
import { DispatchNotices } from '../components/DispatchNotices'
import type { ChannelType } from '@sac/contracts'

/**
 * Pedidos — every marketplace order, with whether the buyer ever talked to us.
 *
 * The contact column carries the weight. A plain "contacted / did not" says almost nothing: a
 * message *before* the order is a question we may have converted, one *after* is support on
 * something already bought. Blending the two is what made the first conversion figure read 35%
 * instead of 24%, so they are shown as different states and never summed.
 */

function money(n: number) {
  return `R$ ${n.toFixed(2).replace('.', ',')}`
}

function shortDate(iso: string) {
  return new Date(iso).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit' })
}

const CONTACT_LABEL: Record<OrderRowDTO['contact']['state'], string> = {
  before: 'Falou antes de comprar',
  after: 'Falou depois da compra',
  none: 'Sem contato',
}

/**
 * The bubble.
 *
 * Solid before the sale, outlined after it, a dash when they never wrote. The shape carries the
 * meaning as well as the colour — colour alone does not survive a colourblind reader, and the
 * title spells it out for everyone else.
 */
function ContactBubble({ contact, onOpen }: { contact: OrderRowDTO['contact']; onOpen: () => void }) {
  if (contact.state === 'none') {
    return (
      <span className="ord-bubble none" title="Sem contato">
        —
      </span>
    )
  }
  const before = contact.state === 'before'
  return (
    <button
      className={`ord-bubble ${before ? 'before' : 'after'}`}
      title={`${CONTACT_LABEL[contact.state]}${contact.at ? ` · ${shortDate(contact.at)}` : ''} — abrir conversa`}
      onClick={onOpen}
    >
      <Icon name="chat" size={13} />
    </button>
  )
}

export function Pedidos({ onOpenConversation }: { onOpenConversation?: (id: string) => void }) {
  const [q, setQ] = useState('')
  const [channel, setChannel] = useState('')
  const [contact, setContact] = useState('')
  const [page, setPage] = useState(0)
  const limit = 50

  const { data: stats } = useQuery({ queryKey: ['orders', 'stats'], queryFn: () => api.orderStats() })
  const { data: rows, isLoading } = useQuery({
    queryKey: ['orders', q, channel, contact, page],
    queryFn: () =>
      api.orders({
        q: q || undefined,
        channel: channel || undefined,
        contact: contact || undefined,
        limit,
        offset: page * limit,
      }),
  })

  const total = stats?.total ?? 0
  const pct = (n: number) => (total ? `${Math.round((n / total) * 100)}%` : '0%')
  const reset = <T,>(set: (v: T) => void) => (v: T) => {
    set(v)
    setPage(0)
  }

  return (
    <div className="crm">
      <div className="crm-head">
        <h2>Pedidos</h2>
        <p>
          Todos os pedidos dos marketplaces, não só os de quem falou com a gente — é essa base
          completa que permite medir conversão. O balão mostra se a pessoa escreveu <b>antes</b> de
          comprar ou <b>depois</b>, e leva direto para a conversa.
        </p>
      </div>

      {/* Acima das estatísticas de propósito: é a única coisa nesta tela que pede ação hoje. */}
      <DispatchNotices />

      <div className="crm-stats">
        <Stat label="Pedidos" value={String(total)} />
        <Stat label="Receita" value={money(stats?.revenue ?? 0)} />
        <Stat label="Falaram antes" value={String(stats?.contactedBefore ?? 0)} sub={pct(stats?.contactedBefore ?? 0)} tone="ok" />
        <Stat label="Falaram depois" value={String(stats?.contactedAfter ?? 0)} sub={pct(stats?.contactedAfter ?? 0)} />
        <Stat label="Sem contato" value={String(stats?.noContact ?? 0)} sub={pct(stats?.noContact ?? 0)} tone="idle" />
      </div>

      <div className="crm-filters">
        <input placeholder="Buscar por pedido ou comprador…" value={q} onChange={(e) => reset(setQ)(e.target.value)} />
        <select value={channel} onChange={(e) => reset(setChannel)(e.target.value)}>
          <option value="">Todos os canais</option>
          <option value="mercadolivre">Mercado Livre</option>
          <option value="shopee">Shopee</option>
        </select>
        <select value={contact} onChange={(e) => reset(setContact)(e.target.value)}>
          <option value="">Qualquer contato</option>
          <option value="before">Falaram antes de comprar</option>
          <option value="after">Falaram depois da compra</option>
          <option value="none">Sem contato</option>
        </select>
      </div>

      {isLoading ? (
        <div className="empty">Carregando…</div>
      ) : !rows?.length ? (
        <div className="empty" style={{ padding: 28 }}>
          Nenhum pedido com esses filtros.
        </div>
      ) : (
        <div className="crm-table">
          <div className="crm-row ord-row crm-th">
            <span />
            <span>Pedido</span>
            <span>Produto</span>
            <span>Comprador</span>
            <span>Canal</span>
            <span>Status</span>
            <span>Valor</span>
            <span>Data</span>
          </div>
          {rows.map((o) => (
            <div className="crm-row ord-row" key={o.id}>
              <ContactBubble
                contact={o.contact}
                onOpen={() => o.contact.conversationId && onOpenConversation?.(o.contact.conversationId)}
              />
              <code className="ord-id">{o.externalOrderId}</code>
              <span className="ord-prod">
                {o.product?.imageUrl && <img src={o.product.imageUrl} alt="" />}
                <span className="t1">
                  {o.product?.name ?? '—'}
                  {o.product && o.product.moreItems > 0 && <span className="cfg-badge">+{o.product.moreItems}</span>}
                </span>
              </span>
              <span className="crm-who">
                <CustomerAvatar seed={o.customer.id} color={o.customer.avatarColor ?? '#888'} name={o.customer.name ?? '?'} size={24} />
                <span className="t1">{o.customer.name ?? o.buyerLabel ?? '—'}</span>
              </span>
              <span>
                <ChannelMark channel={o.channelType as ChannelType} size={15} />
              </span>
              <span className={`pill ${o.statusTone}`}>{o.status}</span>
              <span>{money(o.total)}</span>
              <span className="t2">{shortDate(o.createdAt)}</span>
            </div>
          ))}
        </div>
      )}

      <div className="ord-pager">
        <button className="btn sec sm" disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))}>
          Anterior
        </button>
        <span className="cfg-note">
          {rows?.length ? `${page * limit + 1}–${page * limit + rows.length}` : '0'} de {total}
        </span>
        {/* Enabled on a full page rather than a computed page count: the filtered total is not
            fetched per query, and guessing it would offer a page that turns out to be empty. */}
        <button className="btn sec sm" disabled={(rows?.length ?? 0) < limit} onClick={() => setPage((p) => p + 1)}>
          Próxima
        </button>
      </div>
    </div>
  )
}

function Stat({ label, value, sub, tone }: { label: string; value: string; sub?: string; tone?: 'ok' | 'warn' | 'idle' }) {
  return (
    <div className={`crm-stat ${tone ?? ''}`}>
      <span className="v">{value}</span>
      <span className="l">{label}</span>
      {sub && <span className="s">{sub}</span>}
    </div>
  )
}
